import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad — Rescate de Pareja: Post-Crisis

Última actualización: Abril 2026

1. Información que recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados por nuestro proveedor de pagos seguro.

2. Uso de la información
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu compra digital
• Enviarte el material adquirido por correo electrónico
• Comunicarnos contigo en caso de problemas con tu pedido

3. Protección de datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartir información
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios que nos ayudan a operar nuestro sitio y procesar pagos, quienes están obligados a mantener la confidencialidad.
`.trim();
const termsContent = `
Términos y Condiciones — Rescate de Pareja: Post-Crisis

Última actualización: Abril 2026

1. Aceptación de los términos
Al adquirir y utilizar este producto digital, aceptás estos términos y condiciones en su totalidad. Si no estás de acuerdo, no realices la compra.

2. Descripción del producto
"Rescate de Pareja: Post-Crisis" es un producto digital (eBook en formato PDF) que incluye guías, plantillas y herramientas para mejorar la comunicación en pareja. No constituye asesoramiento psicológico, terapéutico ni profesional.

3. Entrega del producto
El producto se entrega de forma digital e inmediata tras confirmar el pago. Recibirás un enlace de descarga y/o el material por correo electrónico.

4. Política de reembolso
Ofrecemos una garantía de 7 días. Si el contenido no te aporta valor, puedes solicitar la devolución total de tu dinero dentro de este plazo.

5. Uso permitido
El material es para uso personal. Queda prohibida la reproducción, distribución, reventa o publicación del contenido, total o parcial, sin autorización expresa.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-[#0D002B] py-20 border-t border-[#7C3AED]/30 pb-32 md:pb-20" data-id="element-148">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-149">
          <div className="mb-20" data-id="element-150">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-10 font-serif" data-id="element-151">
              ¿Listo para empezar el rescate?
            </h3>
            <a href="https://clavedepaz.com/cart/44108367659107:1" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#7C3AED] text-white px-10 py-5 rounded-lg text-lg font-semibold shadow-[0_10px_25px_-5px_rgba(124,58,237,0.5)] hover:bg-[#6D28D9] transition-colors font-sans tracking-wide" data-id="element-152">
              SÍ, QUIERO ACCEDER AL MATERIAL
            </a>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-[#7C3AED]/30" data-id="element-153">
            <p className="text-[#A78BFA] text-sm font-sans" data-id="element-154">
              © 2026 Rescate de Pareja: Post-Crisis. Todos los derechos
              reservados.
            </p>
            <div className="flex items-center gap-6" data-id="element-155">
              <button onClick={() => setActiveModal('privacy')} className="text-[#A78BFA] hover:text-white text-sm transition-colors font-sans" data-id="element-156">
                Política de Privacidad
              </button>
              <button onClick={() => setActiveModal('terms')} className="text-[#A78BFA] hover:text-white text-sm transition-colors font-sans" data-id="element-157">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-158">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-159">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-[#1A0050] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-[#7C3AED]/30" onClick={e => e.stopPropagation()} data-id="element-160">
              <div className="flex items-center justify-between p-6 border-b border-[#7C3AED]/30" data-id="element-161">
                <h3 className="text-xl font-bold text-white font-serif" data-id="element-162">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 rounded-full bg-[#0D002B] flex items-center justify-center hover:bg-[#7C3AED]/20 transition-colors" data-id="element-163">
                  <XIcon className="w-5 h-5 text-[#C4B5FD]" data-id="element-164" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-165">
                <div className="prose prose-sm max-w-none font-sans" data-id="element-166">
                  {(activeModal === 'privacy' ? privacyContent : termsContent).split('\n').map((line, i) => {
                if (!line.trim()) return <div key={i} className="h-3" data-id="element-167" />;
                if (/^\d+\./.test(line.trim())) {
                  return <h4 key={i} className="text-lg font-bold text-white mt-6 mb-2" data-id="element-168">
                            {line}
                          </h4>;
                }
                if (line.startsWith('Política') || line.startsWith('Términos')) {
                  return <h4 key={i} className="text-lg font-bold text-[#C4B5FD] mb-1" data-id="element-169">
                            {line}
                          </h4>;
                }
                if (line.startsWith('Última')) {
                  return <p key={i} className="text-sm text-[#A78BFA] mb-4" data-id="element-170">
                            {line}
                          </p>;
                }
                if (line.startsWith('•')) {
                  return <p key={i} className="text-[#E0D7FF] pl-4 mb-1" data-id="element-171">
                            {line}
                          </p>;
                }
                return <p key={i} className="text-[#E0D7FF] mb-2 leading-relaxed" data-id="element-172">
                          {line}
                        </p>;
              })}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}